import { TravelExplore } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Chip, TextField, Tooltip } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchContext } from '../../contexts/SearchContext';
import { IMaterialDescriptionComboBoxOption, IMaterialItem, ISearchInput } from '../../interfaces';
import { Values } from '../../utils';
import './index.scss';

const SearchContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },

    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: ['80%'],

    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
    },

    display: "flex"
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    cursor:'pointer',
    zIndex:2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'

}));

const StyledInputBase = styled(TextField)(({ theme }) => ({
    color: 'inherit',
    width: "100%",
    paddingLeft: "3rem",
    paddingRight: ".5rem",
    '& .MuiInput-root:before': { border: "none" },
    '& .MuiInput-root:hover:before': { border: "none" },

    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
    },

}));



export const Search: React.SFC<ISearchInput> = ({
    ...props
}: ISearchInput) => {

    const navigate = useNavigate();
    const searchContext = useContext(SearchContext);

    const startSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {


        if (e.key === "Enter") {
            let keywords = cloneDeep(searchContext.keywords);



            if (searchContext.query.length > 0)
                keywords.push({ value: searchContext.query })
            else if (keywords.length === 0) {
                //support empty search
                keywords.push({ value: " " });
            }

            searchContext.search(true, keywords);
            navigate("/");
        }

        if (e.key === "Escape") {
            clearSearch();
        }

        if (searchContext.keywords.length === 0 && searchContext.query.length === 0)
            clearSearch();

        if (e.key === "Backspace")
            console.log("query", searchContext.keywords, searchContext.query);


    }

    const clearSearch = () => {
        searchContext.setQuery("");
        searchContext.clearResults();
        searchContext.setKeywords([]);
    }

    const onUpdate = (items: (IMaterialItem | string)[]) => {

        let keywordValues = items.map(v => {

            if (typeof (v) === "string") {
                return { value: v }
            } else return v;

        }) as IMaterialItem[]

        searchContext.setKeywords(keywordValues);
        searchContext.setQuery("");

    }

    const initSuggestions = () => {

        if (searchContext.suggestions.length === 0) {

            let terms: IMaterialItem[] = [];

            //get the available options for the searchable properties

            ["sparetypes", "vehicleclasses", "sites", "commercial-agreements", "expectedmaterialreturnstates"].forEach(x =>
                Values.setValues(x as string, (r: IMaterialItem[]) => r.forEach(term => {
                    term.propertyName = x;
                    terms.push(term)
                }))
            );

            //as the descriptions are nested, iterate through each one

            Values.setValues("descriptions", (descriptions: IMaterialDescriptionComboBoxOption[]) => {
                descriptions.forEach(l1 => {

                    let l1Item = l1 as IMaterialItem;
                    l1Item.propertyName = "l1";

                    terms.push(l1 as IMaterialItem);
                    l1.getL2Descriptions?.forEach(l2 => {

                        let l2Item = l2 as IMaterialItem;
                        l2Item.propertyName = "l2";
                        terms.push(l2 as IMaterialItem)
                    })
                })
            }).then(() => { searchContext.setSuggestions(terms); })
        }
    }

    const removable = (item: IMaterialItem) => {

        if (item === undefined) return false;

        if (item.id === undefined) {
            if (item.propertyName) {
                return false;
            }
        }

        return true;
    }

    const formatValue = (value: unknown) => {
        if (Number.isInteger(Date.parse(value as string)))
            return new Date(value as string).toLocaleDateString("en-GB");

        return value;
    }

    useEffect(() => {

        initSuggestions();

    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    return (

        <SearchContainer>
            <Tooltip title={"Switch to" + (searchContext.materialOut ? " inventory search" :" material out search")}>
                <SearchIconWrapper onClick={()=>searchContext.setMaterialOut(!searchContext.materialOut)}>
                    {searchContext.materialOut === true ?
                        <TravelExplore />
                        :
                        <SearchIcon />
                    }
                </SearchIconWrapper>
            </Tooltip>

            <Autocomplete
                id="main-search-bar"
                multiple
                options={searchContext.query?.length > 1 ? searchContext.suggestions : []}
                groupBy={(option) => (option.propertyName as string).toUpperCase()}
                renderOption={(props: any, item: IMaterialItem) =>

                    (<li {...props} key={(item.id as string + item.value)}>{item.value}</li>)

                }
                getOptionLabel={(x: string | IMaterialItem) => { return (x as IMaterialItem).value as string }}
                value={searchContext.keywords}
                freeSolo
                fullWidth
                onChange={(_, items) => { onUpdate(items) }}
                onInputChange={(e: any) => { if (e.target.value) searchContext.setQuery(e.target.value); }}
                onKeyDown={(e) => startSearch(e)}
                renderTags={(value: (IMaterialItem | null | undefined)[], getTagProps) =>
                    (value as IMaterialItem[]).filter(x => x?.value !== undefined).map((option: IMaterialItem, index: number) => (
                        removable(option) ?

                            <Chip
                                {...getTagProps({ index })}
                                variant={"outlined"}
                                color={"secondary"}
                                label={option.value}
                                key={(option.id as string + option.value)}
                            />

                            :

                            <Chip
                                {...getTagProps({ index })}
                                variant={"filled"}
                                color={"primary"}
                                label={option.propertyName + " : " + formatValue(option.value)}
                                key={(option.id as string + option.value)}
                                onDelete={undefined}
                            />

                    ))
                }
                renderInput={(params) =>
                    <StyledInputBase
                        {...params}
                        placeholder={searchContext.materialOut ? "search material out..." : "search inventory..."}
                        required
                        variant="standard"
                        color="secondary"
                        onChange={e => searchContext.setQuery(e.currentTarget.value)}
                        value={searchContext.query}
                        inputProps={{
                            ...params.inputProps,
                            sx: { my: 1, pr: 10, color: "#fff", overflow: "hidden" },
                        }}
                    />
                }
            />
        </SearchContainer>

    );
}